import type {
  ClassifyAsContributionFields,
  ClassifiedRefundDto,
  ClassifyAsExpenditureFields,
  ExcludeFromReportFields,
  ExcludeFromReportResponse,
  ProcessorFeeCheckResponse,
  ReopenBankTransactionRequest,
  ReopenBankTransactionResponse,
  ContributionDto,
  ExpenditureDto,
  CapturedReceiptSuggestionDto,
  TriageItem,
} from '@signalvote/shared';
import {
  classifyAsContributionRequest,
  classifyAsExpenditureRequest,
  excludeFromReportRequest,
  ErrorCode,
} from '@signalvote/shared';
import { apiJson, apiResponse } from './apiFetch';
import { throwStagedRowError } from './useFinanceContributions';
import {
  markBankExcluded,
  markBankReopened,
  editInboxRow,
  withBankLineVersion,
} from './sortInboxOptimistic';
import { defineStatusFlip } from './optimisticStatusFlip';
import { campaignHeaders, defineQuery, defineWrite } from '@/lib/data';

/**
 * Kind predicate selecting a BankLine row in the sort-inbox cache — the row
 * identity the optimistic patch and the concurrency-token write-back both key on
 * (the exclude → reopen → exclude loop's follow-ups read the reconciled version).
 */
const bankTransactionRow = (btId: string) => (item: TriageItem): boolean =>
  item.kind === 'BankLine' && item.bt.id === btId;

/** Thrown when the server returns a 409 — another client touched the BankTransaction. */
export class BankTransactionConcurrencyConflictError extends Error {
  // Always a 409; carried so the transient-vs-terminal split (the write door's transient retry)
  // reads this HTTP response as terminal rather than a retriable network blip.
  status = 409;
  // These endpoints send some 409s with no code, so the class names the stale write itself for isStaleWrite.
  readonly code = ErrorCode.ConcurrencyConflict;
  constructor(message: string) {
    super(message);
    this.name = 'BankTransactionConcurrencyConflictError';
  }
}

/**
 * Thrown when the server returns a 4xx with a stable error `code`
 * (e.g. `BankTransactionAlreadyClassified`, `ContactNotFound`). The panel
 * surfaces the message verbatim and may switch on `code` for follow-ups.
 */
export class BankTransactionClassifyError extends Error {
  code: string;
  status: number;
  constructor(code: string, status: number, message: string) {
    super(message);
    this.name = 'BankTransactionClassifyError';
    this.code = code;
    this.status = status;
  }
}

async function readErrorBody(response: Response): Promise<{ code?: string; error?: string }> {
  return response.json().catch(() => ({}));
}

/**
 * A classify's result. The endpoint returns the record it created, carrying the bank line's new
 * token beside it; null there means no new token, so the one sent is kept.
 */
export interface Classified<T> {
  concurrencyVersion: number;
  classified: T;
}

async function sendClassify<T extends { bankTransactionConcurrencyVersion: number | null }>(
  url: string,
  json: unknown,
  concurrencyVersion: number,
  fallback: string,
): Promise<Classified<T>> {
  const response = await apiResponse(url, { method: 'POST', json });
  if (response.status === 409) {
    const body = await readErrorBody(response);
    throw new BankTransactionConcurrencyConflictError(
      body.error ?? 'This bank line was modified by another user. Refreshing.',
    );
  }
  if (!response.ok) {
    const body = await readErrorBody(response);
    throw new BankTransactionClassifyError(body.code ?? 'Unknown', response.status, body.error ?? fallback);
  }
  const classified = (await response.json()) as T;
  return { concurrencyVersion: classified.bankTransactionConcurrencyVersion ?? concurrencyVersion, classified };
}

export const useClassifyBankTransactionAsContribution = defineWrite({
  row: 'BankTransaction',
  send: (id, concurrencyVersion: number, { data }: { data: Omit<ClassifyAsContributionFields, 'bankTransactionConcurrencyVersion'> }) =>
    sendClassify<ContributionDto>(
      `/api/finance/bank-transactions/${id}/classify-as-contribution`,
      classifyAsContributionRequest({ bankTransactionConcurrencyVersion: concurrencyVersion, ...data }),
      concurrencyVersion,
      'Failed to classify bank transaction as contribution',
    ),
});

export const useClassifyBankTransactionAsExpenditure = defineWrite({
  row: 'BankTransaction',
  // RFR-002: the Refund sub-category returns a ContributionRefund shape
  // (no Expenditure); the other three return an ExpenditureDto.
  send: (id, concurrencyVersion: number, { data }: { data: Omit<ClassifyAsExpenditureFields, 'bankTransactionConcurrencyVersion'> }) =>
    sendClassify<ExpenditureDto | ClassifiedRefundDto>(
      `/api/finance/bank-transactions/${id}/classify-as-expenditure`,
      classifyAsExpenditureRequest({ bankTransactionConcurrencyVersion: concurrencyVersion, ...data }),
      concurrencyVersion,
      'Failed to classify bank transaction as expenditure',
    ),
  // Write the bank line's new token into the cached row too: the line's delete still reads it from there.
  land: (result, { row }) => [editInboxRow(bankTransactionRow(row.id), withBankLineVersion(result.concurrencyVersion))],
});

/**
 * EFR-002 / ADR-014 — "Exclude from report". POSTs to
 * `/api/finance/bank-transactions/{id}/exclude-from-report`, flipping an
 * Unattributed bank line to `ExcludedFromReport` — the single non-reporting
 * resolution that collapses the retired Dismiss / Mark-as-transfer /
 * Pair-as-transfer flows (none affected any rollup or filing). Carries one
 * optional free-text note.
 *
 * Error envelope mirrors the reopen hook: 409 →
 * {@link BankTransactionConcurrencyConflictError}; other 4xx →
 * {@link BankTransactionClassifyError} with the wire `code` (one of
 * `BankTransactionExcludeFromReport*` codes, `'Unknown'` fallback) — a
 * server `NoteTooLong` (400) surfaces its message this way. The write set
 * refreshes the sort inbox, so the row re-renders born-Done in the "Not
 * reported" lens.
 */
export const useExcludeBankTransactionFromReport = defineStatusFlip({
  endpoint: {
    entity: 'BankTransaction',
    send: async (id, concurrencyVersion, fields: Omit<ExcludeFromReportFields, 'concurrencyVersion'>) => {
      const response = await apiResponse(`/api/finance/bank-transactions/${id}/exclude-from-report`, {
        method: 'POST',
        json: excludeFromReportRequest({ concurrencyVersion, ...fields }),
      });
      if (response.status === 409) {
        const body = await readErrorBody(response);
        throw new BankTransactionConcurrencyConflictError(
          body.error ?? 'This bank line was modified by another user. Refreshing.',
        );
      }
      if (!response.ok) {
        const body = await readErrorBody(response);
        throw new BankTransactionClassifyError(
          body.code ?? 'Unknown',
          response.status,
          body.error ?? 'Failed to exclude bank transaction from report',
        );
      }
      return response.json() as Promise<ExcludeFromReportResponse>;
    },
  },
  // Stamp the bank line "Not reported" (Done/Excluded) on click; the row stays in
  // place until the write set's refetch reconciles server truth.
  patch: markBankExcluded,
});

/**
 * Bank-fee-line steering (spec §D7): resolves the payment processor for the bank
 * line and checks whether a fee for that processor is already booked in the period.
 * Read only while "Processing fee" is selected: the body that reads it renders only then.
 */
export const useProcessorFeeCheck = defineQuery({
  key: (bankTransactionId: string) => ['finance', 'bank-transactions', bankTransactionId, 'processor-fee-check'],
  reads: 'GET /api/finance/bank-transactions/{id}/processor-fee-check',
  staleTime: 30_000,
  fetch: (bankTransactionId, { campaignId }) =>
    apiJson<ProcessorFeeCheckResponse>(`/api/finance/bank-transactions/${bankTransactionId}/processor-fee-check`, {
      headers: campaignHeaders(campaignId),
      error: 'Failed to check processor fee status',
    }),
});

/**
 * CAP-006 — the pocket-receipt suggest-match. Fetches the Unattached captured
 * receipts that plausibly back this bank debit (exact amount + date within ±7
 * days, resolved server-side) from
 * GET /api/finance/bank-transactions/{id}/receipt-suggestions.
 *
 * The classify panel passes `skip` unless the Expenditure path is selected, so
 * a contribution / exclude classification never issues the request. An empty
 * array (no pocket match) is the common case and renders nothing.
 *
 * The treasurer picks a suggestion to attach; a suggestion is NEVER
 * auto-attached (finance product direction — suggest, don't auto-apply).
 */
export const useReceiptSuggestions = defineQuery({
  key: (bankTransactionId: string) => ['finance', 'bank-transactions', bankTransactionId, 'receipt-suggestions'],
  reads: 'GET /api/finance/bank-transactions/{id}/receipt-suggestions',
  staleTime: 30_000,
  fetch: (bankTransactionId, { campaignId }) =>
    apiJson<CapturedReceiptSuggestionDto[]>(`/api/finance/bank-transactions/${bankTransactionId}/receipt-suggestions`, {
      headers: campaignHeaders(campaignId),
      error: 'Failed to load receipt suggestions',
    }),
});

/**
 * ATL-005 / ATL-006 — "Reopen" a resolved bank row from the all-time ledger.
 * POSTs to `/api/finance/bank-transactions/{id}/reopen`, flipping an
 * ExcludedFromReport (ADR-014) or DepositOfRecordedChecks row back to
 * Unattributed so the normal classify buttons take over. Single-row (ADR-014).
 *
 * Error envelope (per the ATL-005 controller): 409 carries no wire code
 * (concurrency — surfaced via {@link BankTransactionConcurrencyConflictError});
 * 400 carries one of exactly two codes (`AlreadyClassified` / `NotReopenable`)
 * the caller switches on for treasurer copy; 404 carries no code. Non-409
 * failures throw {@link BankTransactionClassifyError} with the wire `code`
 * (the sibling `'Unknown'` fallback when absent, e.g. a 404 — the caller's
 * error switch falls through to the message in that case).
 */
export const useReopenBankTransaction = defineStatusFlip({
  endpoint: {
    entity: 'BankTransaction',
    send: async (id, concurrencyVersion) => {
      const response = await apiResponse(`/api/finance/bank-transactions/${id}/reopen`, {
        method: 'POST',
        json: { concurrencyVersion } satisfies ReopenBankTransactionRequest,
      });
      if (response.status === 409) {
        const body = await readErrorBody(response);
        throw new BankTransactionConcurrencyConflictError(
          body.error ?? 'This bank line was modified by another user. Refreshing.',
        );
      }
      if (!response.ok) {
        const body = await readErrorBody(response);
        throw new BankTransactionClassifyError(
          body.code ?? 'Unknown',
          response.status,
          body.error ?? 'Failed to reopen bank transaction',
        );
      }
      return response.json() as Promise<ReopenBankTransactionResponse>;
    },
  },
  // Reopen instantly (Done/Excluded → NeedsAction/Classify), back in the
  // un-triaged queue; the write set's refetch reconciles server truth.
  patch: markBankReopened,
});

/**
 * ADR-016 step 2 — permanent single-row delete of a bank transaction, fired
 * from the ledger row's detail. Mirrors the money-row deletes: 204 (no payload),
 * the concurrency token rides as a query param (DELETE carries no body), and it
 * reuses the `StagedRowUpdateError` envelope so the detail dialog translates the
 * refusal codes uniformly (`DeleteBankLineClassified` → 409, etc.).
 *
 * ADR-006 amendment: a raw bank line may now be deleted by a treasurer. There is
 * no tombstone, so re-importing the same statement can resurrect it — the detail
 * confirm says so, so the removal is never a silent surprise.
 */
export const useDeleteBankTransaction = defineWrite({
  row: 'BankTransaction',
  send: async (id, concurrencyVersion: number): Promise<void> => {
    const response = await apiResponse(
      `/api/finance/bank-transactions/${id}?concurrencyVersion=${concurrencyVersion}`,
      { method: 'DELETE' },
    );
    if (!response.ok) {
      await throwStagedRowError(response, 'Failed to delete bank transaction');
    }
    // 204 No Content — no payload.
  },
});
